import { useState, useEffect } from "react";
import { Switch } from "@shadcn-in-nx/ui/switch";
import { Button as UiButton } from "@shadcn-in-nx/ui/button";
import { Bluelagoon } from "@bluelagoon";
import { toast } from "sonner";
import { formatPaymentError } from "../utils/paymentErrorHelper";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import { stripePromise } from "../utils/stripeConfig";

// Inner component that uses Stripe hooks
function BuyCreditsModalInner({
    isOpen,
    onClose,
    platform,
    platformName,
    currentBalance,
    paymentMethod,
    onSuccess,
    t,
}) {
    const stripe = useStripe();

    // Add CSS animation
    const checkAnimation = `
        @keyframes scale-in {
            0% {
                transform: scale(0);
                opacity: 0;
            }
            50% {
                transform: scale(1.2);
            }
            100% {
                transform: scale(1);
                opacity: 1;
            }
        }
        .animate-scale-in {
            animation: scale-in 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
    `;
    const [loading, setLoading] = useState(true);
    const [purchasing, setPurchasing] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [error, setError] = useState(null);
    const [errorDetails, setErrorDetails] = useState(null);
    const [amount, setAmount] = useState("50");
    const [autoReloadEnabled, setAutoReloadEnabled] = useState(true);
    const [threshold, setThreshold] = useState(5);
    const [reloadAmount, setReloadAmount] = useState(25);
    const [hasCustomReload, setHasCustomReload] = useState(false);

    useEffect(() => {
        if (isOpen) {
            loadSettings();
            setShowSuccess(false);
            setError(null);
            setErrorDetails(null);
            setHasCustomReload(false);
        }
    }, [isOpen, platform]);

    // Default reload amount to the purchase amount unless user customized it
    useEffect(() => {
        if (!hasCustomReload) {
            const val = parseFloat(amount) || 0;
            if (val > 0) setReloadAmount(val);
        }
    }, [amount, hasCustomReload]);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const response = await Bluelagoon.getAutoChargeSettings({
                platform,
            });

            if (response?.data?.data) {
                const settings = response.data.data;
                setAutoReloadEnabled(settings.autoChargeEnabled);
                setThreshold(
                    settings.autoChargeThreshold?.toString() || "5"
                );
                if (settings.autoChargeAmount === -1) {
                    // -1 means use half the purchase amount
                    setHasCustomReload(false);
                } else {
                    setReloadAmount(settings.autoChargeAmount?.toString());
                    setHasCustomReload(true);
                }
            }
        } catch (error) {
            console.error("Error loading settings:", error);
        } finally {
            setLoading(false);
        }
    };

    const calculateTotal = () => {
        const amountNum = parseFloat(amount) || 0;
        // For agentui, amount is in dollars, credits = amount / 0.25
        // For others, amount is direct dollar amount
        return amountNum;
    };

    const handlePurchase = async () => {
        try {
            setPurchasing(true);
            setError(null);
            setErrorDetails(null);

            // Save auto-reload settings first
            if (autoReloadEnabled) {
                await Bluelagoon.updateAutoChargeSettings({
                    platform,
                    autoChargeEnabled: true,
                    autoChargeThreshold: parseFloat(threshold),
                    autoChargeAmount: parseFloat(reloadAmount),
                });
            } else {
                // If disabled and user toggled it off, update to disable
                const settings = await Bluelagoon.getAutoChargeSettings({
                    platform,
                });
                if (settings?.data?.data?.autoChargeEnabled) {
                    await Bluelagoon.updateAutoChargeSettings({
                        platform,
                        autoChargeEnabled: false,
                    });
                }
            }

            // Only process credit purchase if amount is provided
            if (amount && parseFloat(amount) > 0) {
                // If payment method on file, use one-click purchase
                if (paymentMethod) {
                    const response = await Bluelagoon.purchaseCreditsWithCard({
                        platform,
                        amount: parseFloat(amount),
                    });

                    // Check for 3DS requirement first (response.data.data contains the actual data)
                    const responseData = response?.data?.data || response?.data;

                    if (responseData?.requiresAction && responseData?.clientSecret) {
                        // Handle 3DS inline using Stripe.js
                        if (!stripe) {
                            setError("Payment system not ready. Please try again.");
                            setPurchasing(false);
                            return;
                        }

                        // Pass the payment method to confirmCardPayment (required after auth failure)
                        const { error: confirmError, paymentIntent } =
                            await stripe.confirmCardPayment(responseData.clientSecret, {
                                payment_method: responseData.paymentMethodId,
                            });

                        if (confirmError) {
                            // 3DS failed or was cancelled
                            const errorInfo = formatPaymentError(
                                confirmError.message || "Authentication failed",
                                { status: 402, code: confirmError.code },
                                t
                            );
                            setError(confirmError.message || "Authentication failed");
                            setErrorDetails(errorInfo);
                            setPurchasing(false);
                            return;
                        }

                        if (paymentIntent.status === "succeeded") {
                            // 3DS succeeded - confirm with backend to add credits
                            const confirmResponse = await Bluelagoon.confirmCreditPayment({
                                paymentIntentId: responseData.paymentIntentId,
                                platform,
                            });

                            if (confirmResponse?.status === 200) {
                                setShowSuccess(true);
                                await new Promise((resolve) => setTimeout(resolve, 800));
                                onClose();
                                if (onSuccess) await onSuccess();
                            } else {
                                setError(confirmResponse?.error || "Failed to add credits");
                                setErrorDetails({
                                    title: t?.('billing.errors.generic.title') || 'Payment Error',
                                    message: confirmResponse?.error || "Payment succeeded but credits could not be added. Please contact support.",
                                    action: "Contact support if credits are not reflected",
                                });
                                setPurchasing(false);
                            }
                        } else {
                            setError(`Payment not completed. Status: ${paymentIntent.status}`);
                            setPurchasing(false);
                        }
                    } else if (response?.status === 200 || response?.data?.status === 200) {
                        // Success - payment completed without 3DS
                        setShowSuccess(true);
                        await new Promise((resolve) => setTimeout(resolve, 800));
                        onClose();
                        if (onSuccess) await onSuccess();
                    } else {
                        // Other errors
                        const errorInfo = formatPaymentError(
                            response?.data?.error || response?.error || "Payment failed",
                            response?.data || response,
                            t
                        );
                        setError(response?.data?.error || response?.error || "Payment failed");
                        setErrorDetails(errorInfo);
                        setPurchasing(false);
                        return;
                    }
                } else {
                    // No payment method - redirect to Stripe checkout
                    const response = await Bluelagoon.createCreditCheckout({
                        amount: parseFloat(amount),
                        platform,
                        successUrl: `${window.location.origin}/billing?credits_success=true&platform=${platform}`,
                        cancelUrl: `${window.location.origin}/billing?credits_canceled=true`,
                    });

                    // Redirect to Stripe checkout
                    if (response?.data?.url || response?.data?.data?.url) {
                        window.location.href = response?.data?.url ? response?.data?.url : response?.data?.data?.url;
                    } else {
                        throw new Error("No checkout URL returned");
                    }
                }
            } else {
                // If no amount, just save settings and refresh
                if (onSuccess) {
                    await onSuccess();
                }
                onClose();
            }
        } catch (error) {
            console.error("Error purchasing credits:", error);

            // Extract the API response from various possible error structures
            // Could be: axios error (error.response.data), thrown API response, or wrapped response
            const apiResponse = error?.response?.data || error;
            const responseData = apiResponse?.data;

            // Check if this is an API error response (has status, error, code properties)
            const isApiError = apiResponse && typeof apiResponse === 'object' &&
                ('status' in apiResponse || 'code' in apiResponse);

            // Check if 3DS authentication is required (even in catch block)
            if (isApiError) {
                const requiresAction =
                    apiResponse?.code === 'requires_action' ||
                    responseData?.requiresAction;

                // If requires action with clientSecret, handle inline 3DS
                if (requiresAction && responseData?.clientSecret && stripe) {
                    const { error: confirmError, paymentIntent } =
                        await stripe.confirmCardPayment(responseData.clientSecret);

                    if (!confirmError && paymentIntent?.status === "succeeded") {
                        const confirmResponse = await Bluelagoon.confirmCreditPayment({
                            paymentIntentId: responseData.paymentIntentId,
                            platform,
                        });

                        if (confirmResponse?.status === 200) {
                            setShowSuccess(true);
                            await new Promise((resolve) => setTimeout(resolve, 800));
                            onClose();
                            if (onSuccess) await onSuccess();
                            return;
                        }
                    }

                    if (confirmError) {
                        const errorInfo = formatPaymentError(
                            confirmError.message || "Authentication failed",
                            { status: 402, code: confirmError.code },
                            t
                        );
                        setError(confirmError.message || "Authentication failed");
                        setErrorDetails(errorInfo);
                        setPurchasing(false);
                        setShowSuccess(false);
                        return;
                    }
                }
            }

            const errorMessage = isApiError
                ? (apiResponse?.error || "Failed to purchase credits")
                : (error?.message || "Failed to purchase credits");
            const errorInfo = formatPaymentError(errorMessage, isApiError ? apiResponse : { status: 500 }, t);
            setError(errorMessage);
            setErrorDetails(errorInfo);
            setPurchasing(false);
            setShowSuccess(false);
        }
    };

    if (!isOpen) return null;

    const total = calculateTotal();
    const creditsToReceive =
        platform === "agentui" ? total / 0.25 : total / 0.25;

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4" onClick={onClose}>
                <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-12 flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
                </div>
            </div>
        );
    }

    return (
        <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
            onClick={onClose}
        >
            <style>{checkAnimation}</style>
            <div
                className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b">
                    <h2 className="text-2xl font-bold text-gray-900">
                        {t("billing.addCredits") || "Add credits"}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                    >
                        ×
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Amount Input */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t("billing.amount") || "Amount"}
                        </label>
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                                $
                            </span>
                            <input
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                                className="w-full pl-8 pr-3 py-2 border-2 border-blue-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="50"
                                min="1"
                            />
                        </div>
                        {platform === "agentui" &&
                            amount &&
                            parseFloat(amount) > 0 && (
                                <p className="text-sm text-gray-600 mt-2">
                                    {t("billing.creditsToReceive") ||
                                        "Credits to receive"}
                                    :{" "}
                                    <span className="font-semibold text-gray-900">
                                        {(parseFloat(amount) / 0.25).toFixed(0)}{" "}
                                        {t("billing.credits") || "credits"}
                                    </span>
                                </p>
                            )}
                    </div>

                    {/* Auto Reload Toggle */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <div>
                                <div className="font-medium text-gray-900">
                                    {t("billing.autoReloadCredits") ||
                                        "Auto reload credits"}
                                </div>
                                <p className="text-sm text-gray-600 mt-1">
                                    {t("billing.autoReloadDescription") ||
                                        "Avoid service disruptions by auto-reloading credits when your balance reaches a specified minimum"}
                                </p>
                            </div>
                            <Switch
                                checked={autoReloadEnabled}
                                onCheckedChange={setAutoReloadEnabled}
                            />
                        </div>
                    </div>

                    {/* Auto Reload Settings */}
                    {autoReloadEnabled && (
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t("billing.whenBalanceReaches") ||
                                        "When balance reaches:"}
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                                        $
                                    </span>
                                    <input
                                        type="number"
                                        value={threshold}
                                        onChange={(e) =>
                                            setThreshold(e.target.value)
                                        }
                                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        min="0"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {t("billing.bringBalanceBackTo") ||
                                        "Bring balance back up to:"}
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                                        $
                                    </span>
                                    <input
                                        type="number"
                                        value={reloadAmount}
                                        onChange={(e) => {
                                            setReloadAmount(e.target.value);
                                            setHasCustomReload(true);
                                        }}
                                        className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        min="1"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Pricing Breakdown */}
                    <div className="space-y-2 pt-4 border-t">
                        <div className="flex justify-between text-gray-700">
                            <span>{t("billing.subtotal") || "Subtotal"}</span>
                            <span>${total.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-gray-700">
                            <span>
                                {t("billing.estimatedTaxes") ||
                                    "Estimated taxes"}
                            </span>
                            <span>$0.00</span>
                        </div>
                        <div className="flex justify-between text-lg font-bold text-gray-900 pt-2 border-t">
                            <span>{t("billing.total") || "Total"}</span>
                            <span>${total.toFixed(2)}</span>
                        </div>
                    </div>

                    {/* Error Display */}
                    {error && errorDetails && (
                        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                            <div className="flex items-start">
                                <div className="flex-shrink-0">
                                    <svg
                                        className="w-5 h-5 text-red-500 mt-0.5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                </div>
                                <div className="ml-3 flex-1">
                                    <h4 className="text-sm font-bold text-red-800">
                                        {errorDetails.title}
                                    </h4>
                                    <p className="text-sm text-red-700 mt-1.5 leading-relaxed">
                                        {errorDetails.message}
                                    </p>
                                    {errorDetails.action && (
                                        <p className="text-xs text-red-600 mt-2 font-medium">
                                            💡 {errorDetails.action}
                                        </p>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setError(null);
                                        setErrorDetails(null);
                                    }}
                                    className="ml-3 flex-shrink-0 text-red-400 hover:text-red-600"
                                >
                                    <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M6 18L18 6M6 6l12 12"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Payment Method Info */}
                    {paymentMethod && (
                        <p className="text-sm text-gray-600 text-center">
                            {t("billing.willBeChargedTo") ||
                                "Will be charged to"}{" "}
                            <span className="font-medium capitalize">
                                {paymentMethod.brand}
                            </span>{" "}
                            {t("billing.endingIn") || "ending in"}{" "}
                            {paymentMethod.last4}
                        </p>
                    )}

                    {/* Buy Button */}
                    <UiButton
                        onClick={handlePurchase}
                        disabled={purchasing || showSuccess}
                        className="w-full bg-gray-900 hover:bg-gray-800 text-white h-12 text-base font-semibold relative"
                    >
                        {showSuccess ? (
                            <span className="flex items-center justify-center gap-2">
                                <svg
                                    className="w-5 h-5 animate-scale-in"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={3}
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                                {t("billing.success") || "Success!"}
                            </span>
                        ) : purchasing ? (
                            t("billing.processing") || "Processing..."
                        ) : amount && parseFloat(amount) > 0 ? (
                            `${t("billing.buy") || "Buy"} $${total.toFixed(
                                2
                            )} ${t("billing.ofCredits") || "of credits"}`
                        ) : (
                            t("billing.saveSettings") || "Save settings"
                        )}
                    </UiButton>

                    {/* Disclaimer */}
                    <p className="text-xs text-gray-500 text-center">
                        {t("billing.creditsPurchaseDisclaimer") ||
                            "Credits are consumed with API, Claude Code and Workbench usage, and expire 1 year after purchase."}
                    </p>
                </div>
            </div>
        </div>
    );
}

// Wrapper component that provides Stripe Elements context
export function BuyCreditsModal(props) {
    // Don't render Elements provider if modal is closed
    if (!props.isOpen) return null;

    return (
        <Elements stripe={stripePromise}>
            <BuyCreditsModalInner {...props} />
        </Elements>
    );
}
